import ResponseHelper from './response-helper';
import Formatter from '../formats';
/**
 * Lib that manages communication with Vizzto server related to payments.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

/**
 * @typedef Address
 * @type {Object}
 * @property {String} zip Zip code digits (00000000)
 * @property {String} street
 * @property {String} number
 * @property {String} [extra] Address complement
 * @property {String} district
 * @property {String} city
 * @property {String} state 2-chars state identifier
 * @property {String} country 2-chars country identifier
 */

/**
 * @typedef PaymentData
 * @type {Object}
 * @property {String} type Payment type, currently only 'credit-card'
 * @property {Object} creditCard
 * @property {Object} creditCard.owner
 * @property {Boolean} creditCard.owner.same Whether the owner of credit card and user are the same
 * @property {String} creditCard.owner.document CPF
 * @property {Address} creditCard.owner.address
 * @property {String} creditCard.owner.birthday Birthday digits (DDMMYYYY)
 * @property {String} creditCard.number Credit card number (digits only)
 * @property {String} creditCard.name Name on card
 * @property {String} creditCard.expiration Expiration date digits (MMYY)
 * @property {String} creditCard.cvv Credit card CVV digits
 */

function userPaymentDataMapper(item) {
  return {
    type: item.paymentType, // Retorna sempre 1, que é cartão de crédito
    paymentMethodId: item.paymentMethodId,
    holderName: item.holderName,
    selected: item.selected,
    lastDigits: item.last4Digits,
    expiration: item.expireDate
  };
}

function getPaymentStatementMapper(item) {
  /* let services = []
  item.serviceData.forEach((serviceData) => {
    service.push(serviceData.productName)
  }) */
  return {
    id: item.invoiceId,
    type: item.paymentType, // TODO retorna sempre 1, que é cartão de crédito
    payerId: item.last4Digits, // Something to identify what was used to pay (in credit card case, last 4 digits)
    transactionId: ' ', // TODO não existe hoje
    value: item.grandTotal,
    description: item.serviceData, // TODO é um array com informações dos conteudos que foram comprados billId, serviceId, productName, totals
    paymentDate: item.created,
    invoiceStatus: item.invoiceStatus, // TODO OPEN, PENDING, PAID, CANCELLED, REVERSED
    paymentStatus: item.paymentStatus
  };
}

export default (axios) => ({
  /**
   * Gets user's payment data.
   * @param {String} userToken
   * @returns {Promise<{code: number, data: {payment: {lastDigits: string, type: string}}, message: string}>}
   */
  getPaymentData(userToken) {
    return axios({
      method: 'get',
      url: `payments/method`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const payments = [];
        response.data.forEach((item) => {
          payments.push(userPaymentDataMapper(item));
        });
        return Promise.resolve({
          data: payments,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets log of payments.
   * @param {String} userToken
   * @param {Number} page
   * @param {Number} pageSize
   * @returns {Promise<{code: number, data: {total: number, registers: [{payerId: string, description: string, id: string, type: string, value: number, transactionId: string}]}, message: string}>}
   */
  getPaymentStatement(userToken, page, pageSize) {
    return axios({
      method: 'get',
      url: `payments/invoices/${page}/${pageSize}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const registers = [];
        response.data.content.forEach((item) => {
          registers.push(getPaymentStatementMapper(item));
        });
        return Promise.resolve({
          data: {
            total: response.data.totalElements,
            registers
          },
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Updates (actually, adds a new) credit card.
   * @param {String} gatewayPublicKey
   * @param {String} userToken
   * @param {PaymentData} paymentData
   * @param {gateway} gateway
   * @returns {Promise<{code: number, message: string}>}
   */
  updateCreditCard(userToken, gatewayPublicKey, gateway, paymentData) {
    let ccBrand;
    if (!gateway) {
      const error = 'Gateway de pagamento não configurado';
      return Promise.reject(ResponseHelper.getErrorResponse(error));
    }

    const cc = paymentData.creditCard;
    const ccExp = cc.expiration.match(/(\d{2})(\d{2,4})/);

    switch (gateway) {
      case 'PAGARME':
        return axios({
          method: 'post',
          url: `https://api.pagar.me/core/v5/tokens?appId=${gatewayPublicKey}`,
          data: {
            type: 'card',
            card: {
              number: cc.number,
              holder_name: cc.name,
              exp_month: `${ccExp[1]}`,
              exp_year: `${ccExp[2]}`,
              cvv: cc.cvv,
              label: ccBrand
            }
          }
        })
          .then((hash) => {
            return axios({
              method: 'post',
              url: `payments/method`,
              headers: {
                utoken: userToken
              },
              data: {
                paymentType: 1, // Sempre enviar 1 que é o cartão de credito
                selected: true, // True/false para setar o cartao default ou nao
                brand: hash.data.card.brand,
                hash: hash.data.id,
                last4Digits: cc.number.slice(-4),
                holderName: cc.name,
                taxDocumentType: 'CPF',
                taxDocument: cc.owner.document, // Numero CPF
                birthday: Formatter.formatDateStringToParameter(
                  cc.owner.birthday
                ),
                expireDate: `${ccExp[1]}/${ccExp[2]}`,
                street: cc.owner.address.street,
                streetNumber: cc.owner.address.number,
                complement: cc.owner.address.extra,
                district: cc.owner.address.district,
                city: cc.owner.address.city,
                state: cc.owner.address.state,
                country: cc.owner.address.country,
                zipCode: cc.owner.address.zip
              }
            });
          })
          .then(function () {
            return Promise.resolve({
              data: {},
              code: 0,
              message: 'OK'
            });
          })
          .catch(function (error) {
            return Promise.reject(ResponseHelper.getErrorResponse(error));
          });

      case 'MOIP':
        return import('../moip-helper.js')
          .then((mod) => {
            const cc = paymentData.creditCard;
            ccBrand = mod.default.getCreditCardBrand(cc.number);
            return mod.default.generateMoipHash(
              gatewayPublicKey,
              cc.number,
              cc.cvv,
              cc.expiration
            );
          })
          .then((hash) => {
            const cc = paymentData.creditCard;
            const ccExp = cc.expiration.match(/(\d{2})(\d{2,4})/);
            return axios({
              method: 'post',
              url: `payments/method`,
              headers: {
                utoken: userToken
              },
              data: {
                paymentType: 1, // Sempre enviar 1 que é o cartão de credito
                selected: true, // True/false para setar o cartao default ou nao
                brand: ccBrand,
                hash,
                last4Digits: cc.number.slice(-4),
                holderName: cc.name,
                taxDocumentType: 'CPF',
                taxDocument: cc.owner.document, // Numero CPF
                birthday: Formatter.formatDateStringToParameter(
                  cc.owner.birthday
                ),
                expireDate: `${ccExp[1]}/${ccExp[2]}`,
                street: cc.owner.address.street,
                streetNumber: cc.owner.address.number,
                complement: cc.owner.address.extra,
                district: cc.owner.address.district,
                city: cc.owner.address.city,
                state: cc.owner.address.state,
                country: cc.owner.address.country,
                zipCode: cc.owner.address.zip
              }
            });
          })
          .then(function () {
            return Promise.resolve({
              data: {},
              code: 0,
              message: 'OK'
            });
          })
          .catch(function (error) {
            return Promise.reject(ResponseHelper.getErrorResponse(error));
          });

      default:
      //
    }
  },

  deleteCreditCard(userToken, paymentMethodId) {
    return axios({
      method: 'delete',
      url: `payments/method/${paymentMethodId}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {},
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  setDefaultCreditCard(userToken, paymentMethodId) {
    return axios({
      method: 'get',
      url: `payments/method/${paymentMethodId}/preferencial`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: userPaymentDataMapper(response.data),
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   *
   * @param {String} gatewayPublicKey
   * @param cc
   * @returns {*}
   */
  validatePagarmeCard(gatewayPublicKey, cc) {
    const ccExp = cc.expiration.match(/(\d{2})(\d{2,4})/);
    let ccBrand;

    return axios({
      method: 'post',
      url: `https://api.pagar.me/core/v5/tokens?appId=${gatewayPublicKey}`,
      data: {
        type: 'card',
        card: {
          number: cc.number,
          holder_name: cc.name,
          exp_month: `${ccExp[1]}`,
          exp_year: `${ccExp[2]}`,
          cvv: cc.cvv,
          label: ccBrand
        }
      }
    })
      .then((response) => {
        return response;
      })
      .catch(function (error) {
        const error_option = error.response.data.errors;
        let errorMessage;

        Object.keys(error_option).forEach((key) => {
          switch (key) {
            case 'request.card.number':
              errorMessage = 'Número de cartão inválido';
              break;

            case 'request.card.holder_name':
              errorMessage =
                'Você não pode usar números ou caracteres especiais no nome do cartão';
              break;

            case 'request.card.exp_month':
              errorMessage = 'O campo mês de validade deve estar entre 1 e 12';
              break;

            case 'request.card.exp_year':
              errorMessage = 'O campo ano de validade é inválido';
              break;

            case 'request.card.cvv':
              errorMessage = 'O campo código de segurança é inválido';
              break;

            case 'request.card':
              errorMessage = 'Cartão inválido';
              break;

            default:
              errorMessage = 'Cartão inválido';
              break;
          }

          if (error_option[key][0] === 'Card expired.') {
            errorMessage = 'Cartão expirado';
          }
        });

        return Promise.reject(errorMessage);
      });
  },

  /**
   *
   * @param  {String} cardInfo
   * @returns {*}
   */
  validateMoipCard(cardInfo) {
    return import('../moip-helper.js')
      .then((mod) => {
        const cc = cardInfo;
        return mod.default.validateCard(
          cc.name,
          cc.number,
          cc.cvv,
          cc.expiration
        );
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }
});
